/**
 * Romanize Indic-script text into English letters the way people type Hindi
 * in chat ("chalne mein thodi si madad"), so voice transcripts read like the
 * AI's own Hinglish lines. Port of services/stt-whisper/romanize.py (Indic
 * part): every Indic block is shifted onto Devanagari and romanized with
 * Hindi-style schwa deletion (kal, band, karna — not kala, banda, karanaa).
 * Latin and other scripts pass through unchanged.
 */
const INDIC_BLOCKS = [0x0980, 0x0a00, 0x0a80, 0x0b00, 0x0b80, 0x0c00, 0x0c80, 0x0d00];
const CONSONANTS = {
    'क': 'k', 'ख': 'kh', 'ग': 'g', 'घ': 'gh', 'ङ': 'n',
    'च': 'ch', 'छ': 'chh', 'ज': 'j', 'झ': 'jh', 'ञ': 'n',
    'ट': 't', 'ठ': 'th', 'ड': 'd', 'ढ': 'dh', 'ण': 'n',
    'त': 't', 'थ': 'th', 'द': 'd', 'ध': 'dh', 'न': 'n', 'ऩ': 'n',
    'प': 'p', 'फ': 'ph', 'ब': 'b', 'भ': 'bh', 'म': 'm',
    'य': 'y', 'र': 'r', 'ऱ': 'r', 'ल': 'l', 'ळ': 'l', 'ऴ': 'zh', 'व': 'v',
    'श': 'sh', 'ष': 'sh', 'स': 's', 'ह': 'h',
};
// ड़ / ढ़ as typed in Hinglish chat: thodi, bada, padhai (not thori / bara).
const NUKTA_FORMS = { 'क': 'q', 'ख': 'kh', 'ग': 'gh', 'ज': 'z', 'ड': 'd', 'ढ': 'dh', 'फ': 'f', 'य': 'y' };
const VOWELS = {
    'अ': 'a', 'आ': 'a', 'इ': 'i', 'ई': 'i', 'उ': 'u', 'ऊ': 'u', 'ऋ': 'ri', 'ॠ': 'ri', 'ऌ': 'li',
    'ऍ': 'e', 'ए': 'e', 'ऐ': 'ai', 'ऑ': 'o', 'ओ': 'o', 'औ': 'au', 'ऎ': 'e', 'ऒ': 'o',
};
const MATRAS = {
    'ा': 'a', 'ि': 'i', 'ी': 'i', 'ु': 'u', 'ू': 'u', 'ृ': 'ri', 'ॄ': 'ri', 'ॅ': 'e', 'े': 'e',
    'ै': 'ai', 'ॉ': 'o', 'ो': 'o', 'ौ': 'au', 'ॆ': 'e', 'ॊ': 'o',
};
const HALANT = '्';
const NUKTA = '़';
const ANUSVARA = 'ं';
const CHANDRABINDU = 'ँ';
const VISARGA = 'ः';
const PUNCT = { '।': '.', '॥': '.', 'ऽ': '' };
const LABIALS = new Set(['p', 'ph', 'b', 'bh', 'm']);
/** Common words whose everyday Hinglish spelling the rules don't produce. */
const COMMON = {
    'में': 'mein', 'मैं': 'main', 'हैं': 'hain', 'हूँ': 'hoon', 'हूं': 'hoon', 'नहीं': 'nahi', 'नही': 'nahi',
    'क्यों': 'kyon', 'यहाँ': 'yahan', 'यहां': 'yahan', 'वहाँ': 'vahan', 'वहां': 'vahan', 'कहाँ': 'kahan', 'कहां': 'kahan',
    'हाँ': 'haan', 'हां': 'haan', 'ठीक': 'theek', 'जी': 'ji', 'आप': 'aap', 'कीजिए': 'kijiye', 'दीजिए': 'dijiye',
    'चाहिए': 'chahiye', 'लिए': 'liye', 'किए': 'kiye', 'दिए': 'diye', 'गए': 'gaye', 'गई': 'gayi', 'हुए': 'hue',
};
function toDevanagari(text) {
    let out = '';
    for (const ch of text) {
        const cp = ch.codePointAt(0);
        const base = INDIC_BLOCKS.find((b) => cp >= b && cp < b + 0x80);
        out += base !== undefined ? String.fromCodePoint(cp - base + 0x0900) : ch;
    }
    return out;
}
/** Devanagari word → [consonant, vowel, coda] units; vowel null = inherent a. */
function syllables(word) {
    const units = [];
    const chars = [...word];
    for (let i = 0; i < chars.length; i++) {
        const ch = chars[i];
        let nxt = chars[i + 1] ?? '';
        if (CONSONANTS[ch] !== undefined) {
            let cons = CONSONANTS[ch];
            if (nxt === NUKTA) {
                cons = NUKTA_FORMS[ch] ?? cons;
                i++;
                nxt = chars[i + 1] ?? '';
            }
            if (nxt === HALANT) {
                units.push([cons, '', '']);
                i++;
                continue;
            }
            if (MATRAS[nxt] !== undefined) {
                units.push([cons, MATRAS[nxt], '']);
                i++;
                continue;
            }
            units.push([cons, null, '']);
        }
        else if (VOWELS[ch] !== undefined) {
            units.push(['', VOWELS[ch], '']);
        }
        else if (ch === ANUSVARA || ch === CHANDRABINDU) {
            if (units.length)
                units[units.length - 1][2] += 'N'; // n / m resolved below
            else
                units.push(['', '', 'n']);
        }
        else if (ch === VISARGA) {
            if (units.length)
                units[units.length - 1][2] += 'h';
        }
        else if (MATRAS[ch] !== undefined) {
            units.push(['', MATRAS[ch], '']);
        }
        else if (ch !== HALANT && ch !== NUKTA) {
            units.push(['', '', PUNCT[ch] ?? ch]);
        }
    }
    return units;
}
function romanizeWord(word) {
    const known = COMMON[word];
    if (known)
        return known;
    const units = syllables(word);
    const hasVowel = (u) => u[1] === null || u[1] !== '';
    const schwa = (u) => Boolean(u[0]) && u[1] === null && !u[2];
    if (units.length > 1) {
        if (schwa(units[units.length - 1]))
            units[units.length - 1][1] = '';
        // Medial: V C(a) C V → drop the a, right to left (karana → karna).
        for (let k = units.length - 2; k > 0; k--) {
            const u = units[k];
            const prev = units[k - 1];
            const next = units[k + 1];
            if (schwa(u) && hasVowel(prev) && next[0] && hasVowel(next))
                u[1] = '';
        }
    }
    return units.map(([cons, vowel, coda], k) => {
        let c = coda;
        if (c.includes('N')) {
            const following = units[k + 1]?.[0] ?? '';
            c = c.replace(/N/g, LABIALS.has(following) ? 'm' : 'n');
        }
        return cons + (vowel === null ? 'a' : vowel) + c;
    }).join('');
}
const INDIC_RE = /[ऀ-ൿ]/;
// Devanagari letters without the dandas (। ॥), which end a word like a full stop.
const DEVA_WORD_RE = /([ऀ-ॣ०-ॿ]+)/;
/** True when the text has any Indic-script letters. */
export function hasIndicScript(text) {
    return INDIC_RE.test(text);
}
/** Indic script → English letters ("चलने में" → "chalne mein"); other text unchanged. */
export function romanizeIndic(text) {
    if (!text || !hasIndicScript(text))
        return text;
    const dev = toDevanagari(text.normalize('NFC'));
    // split() with a capture group: odd indexes are the Devanagari words.
    const out = dev.split(DEVA_WORD_RE)
        .map((p, i) => (i % 2 === 1 ? romanizeWord(p) : p.replace(/[।॥]/g, '.')))
        .join('');
    return out.replace(/\s+([.,!?])/g, '$1').replace(/\s{2,}/g, ' ').trim();
}
//# sourceMappingURL=romanize.js.map